// src/features/reports/ReportsPage.jsx
//
// Reports landing — gradient-banner tile cards, one per report.
// A toolbar above the grid provides search, category filtering, and a
// favorites-only toggle. Grid is the only layout.
//
// Ordering: report tiles follow the admin-configured order persisted in
// feature_flags.sort_order via PATCH /api/admin/feature-flags/reorder.
// FeatureFlagContext already fetches and exposes reportFlags sorted by that
// column, so orderedTiles derives directly from it — no per-user preference.
//
// Visibility: only flags with enabled=true in FeatureFlagContext.reportFlags
// are rendered.
//
// Favorites are persisted per-user via PUT /api/preferences/reports_favorites.
//
// REPORTS-TILES-V2-HERO-NUMBER additions:
//   • WPR RAG roll-up: fetches projects + locations + milestones once on
//     mount, calls buildWprRows (same assembly fn as WeeklyPortfolioReview),
//     derives { onTrack, atRisk, critical } and passes as ragCounts to the WPR
//     tile.
//   • liveCount / liveLabel: passed to Headcount Trend, Open Roles, Space Fit,
//     and Foot Traffic tiles.
//
// REPORT-CARD-KPI-LOADING-SPINNER: All five KPI state slots use a 3-way sentinel:
//   "loading"  → initial; actively fetching — tile renders a spinner
//   <value>    → resolved; number/object with usable data — tile animates in
//   null       → failed or no usable value — tile shows honest "—"
// Each useEffect sets null in .catch and when the response has no usable value,
// so a failed fetch never leaves the tile stuck showing a spinner.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { Star } from "lucide-react";
import { getPreference, setPreference } from "../../data/preferencesClient.js";
import { getOpenRolesReport } from "../../data/openRolesAdapter.js";
import {
  getCostLeakageReport,
  getDataCoverage,
  getFootTrafficReport,
  getHeadcountTrendReport,
  getSpaceFitReport,
  getUtilizationReport,
} from "../../data/reportsClient.js";
import { getProjects, getAllNextMilestones } from "../../data/projectsClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { buildWprRows } from "./wprFilters.js";
import { S } from "../../app/styles.js";
import { T } from "../../app/tokens.js";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { ReportTile } from "./ReportTile.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { useReportViewState } from "../../v2/hooks/useReportViewState.js";
import { REPORT_LANDING_KEY } from "../../services/portfolioStatePersistence.js";
import { getAvailableReportKeys, getReportNavEligibility } from "./reportNavEligibility.js";
import { fmtShortMonth } from "./dataCoverage/coverageFormat.js";


// SVG thin-line paths (viewBox 0 0 24 24, stroke-width 1.6, linecap/linejoin round).
// Lucide icon family — matching the HTML prototype.
const REPORT_TILES = [
  {
    key: "report-data-coverage",
    title: "Data coverage",
    subtitle: "What's been ingested, when it last refreshed, and where the gaps are",
    svgPath: "M4 4h16v16H4zM8 8h8M8 12h8M8 16h4",
    description: "What's been ingested, when it last refreshed, and where the gaps are",
    tags: ["Data", "Feeds", "Coverage"],
  },
  {
    key: "report-wpr",
    title: "Weekly portfolio review",
    subtitle: "Projects & Sites Roll-up",
    // Clipboard icon (Lucide: clipboard-list)
    svgPath: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 4 0M9 12h6M9 16h4",
    description:
      "RAG-sorted roll-up of active projects and operational sites - one editorial update per row, next milestone, and owner at a glance.",
    tags: ["Projects", "Sites", "RAG Status"],
  },
  {
    key: "report-headcount-trend",
    title: "Headcount trend",
    subtitle: "Per-import view",
    // Trending-up icon (Lucide: trending-up)
    svgPath: "M23 6l-9.5 9.5-5-5L1 18M17 6h6v6",
    description:
      "How portfolio headcount changed across every Workday import - aggregate portfolio chart and a per-office matrix with import-over-import deltas.",
    tags: ["Headcount", "Workday", "Trend"],
  },
  {
    key: "report-open-roles",
    title: "Open Roles",
    subtitle: "Greenhouse · Live",
    // Briefcase icon (Lucide: briefcase)
    svgPath: "M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2",
    description:
      "Live Greenhouse open roles fractionalized across markets - surfaces forward hiring demand alongside market capacity pressure.",
    tags: ["Hiring", "Greenhouse", "Capacity"],
  },
  {
    key: "report-space-fit",
    title: "Space Fit",
    subtitle: "Desk demand · Build density",
    // Maximize-2 icon (Lucide: maximize-2) — fits the space/fit theme
    svgPath: "M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7",
    description:
      "Office-level space fit verdicts across the portfolio - how well each location's configuration matches its current and projected demand.",
    tags: ["Offices", "Space", "Fit"],
  },
  {
    key: "report-cost-leakage",
    title: "Cost Leakage",
    subtitle: "Spend that isn't buying productive, occupied space.",
    svgPath: "M3 3v18h18M7 14l4-4 4 4 6-7M17 7h4v4",
    description:
      "See where portfolio spend is not supporting productive, occupied space.",
    tags: ["Offices", "Cost", "Space"],
  },
  {
    key: "report-rto-compliance",
    title: "RTO Compliance",
    subtitle: "Employee headline · 10+ / 30+ days",
    svgPath: "M4 4h16v16H4zM8 12l3 3 5-6",
    description:
      "Read-only monthly, quarterly, and weekly return-to-office compliance, with Employee headline results and contractor reference classes kept together.",
    tags: ["Attendance", "Compliance", "Employee"],
  },
  {
    key: "report-foot-traffic",
    title: "Foot Traffic",
    subtitle: "All badge-ins · Operations",
    svgPath: "M3 21h18M5 21V10l7-6 7 6v11M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01",
    description:
      "Observed office-local building presence for food, snacks, janitorial, and other operations staffing — all badge-ins, without an invented employee/visitor split.",
    tags: ["Attendance", "Operations", "Badge-ins"],
  },
  {
    key: "report-utilization",
    title: "Attendance & Utilization",
    subtitle: "Seat & attendance · Portfolio",
    svgPath: "M4 19V5M4 19h16M8 16v-5M12 16V8M16 16V6M20 16V3",
    description:
      "Attendance is people present divided by assigned headcount; seat utilization is people present divided by installed seats. Measures use Monday to Friday selected-feed observations, excluding Ripple-wide closures and holidays for each office's country. Missing feed days are not counted as zero.",
    tags: ["Attendance", "Seats", "Operations"],
  },
];

export const REPORT_DESCRIPTIONS = Object.fromEntries(
  REPORT_TILES.map(({ key, description }) => [key, description])
);

// Shared report detail metadata. AppShell owns the global title row while
// ReportShell owns only the back/pager row, so both paths use this catalog
// rather than maintaining separate title or subtitle maps.
export const REPORT_METADATA = Object.fromEntries(
  REPORT_TILES.map(({ key, title, subtitle }) => [key, { title, subtitle }])
);

// Display title for each report key — used by AppShell to populate the
// Prev/Next tooltip text on report detail pages.
export const REPORT_TILE_TITLES = Object.fromEntries(
  Object.entries(REPORT_METADATA).map(([key, { title }]) => [key, title])
);

// Category filter — one entry per report, used by the toolbar's "All
// categories" dropdown. Categories are a coarse grouping distinct from the
// per-tile `tags`; kept as a flat key→label map so the dropdown options and
// the filter predicate share one source of truth.
const REPORT_CATEGORIES = {
  "report-data-coverage":       "Data",
  "report-wpr":                "Portfolio",
  "report-headcount-trend":    "Workforce",
  "report-open-roles":         "Hiring",
  "report-space-fit":          "Offices",
  "report-cost-leakage":       "Offices",
  "report-rto-compliance":     "Workforce",
  "report-foot-traffic":        "Workforce",
  "report-utilization":         "Workforce",
};

// Fast key → tile lookup.
const TILE_MAP = Object.fromEntries(REPORT_TILES.map((t) => [t.key, t]));

// Keep the report detail pager on the same enabled, admin-configured sequence
// as the landing page. The feature-flag context already returns reportFlags in
// sort_order, so this helper is the single canonical mapping for both entry
// points.
export function getOrderedReportKeys(reportFlags, flagsReady = true, dataCoverageAllowed = false) {
  if (!flagsReady || !Array.isArray(reportFlags)) return [];
  return reportFlags
    .filter((f) => f.enabled)
    .map((f) => f.key)
    .filter((key) => TILE_MAP[key])
    .filter((key) => key !== "report-data-coverage" || dataCoverageAllowed === true);
}

// Field descriptors for useReportViewState — defines URL params, valid values,
// and defaults for the Reports landing page toolbar state.
const LANDING_VIEW_STATE_FIELDS = [
  { param: "q",        default: "" },
  { param: "category", default: "all" },
];

function previousCompletedCalendarMonthRange(now = new Date()) {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const from = new Date(Date.UTC(year, month - 1, 1));
  const to = new Date(Date.UTC(year, month, 0));
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function costLeakageTotal(report) {
  const records = report?.records;
  if (report?.financialsVisible === false || !Array.isArray(records) || records.length === 0) {
    return null;
  }
  const hasUsableCost = records.some((record) =>
    record?.annualCost != null
    && record.annualCost !== ""
    && Number.isFinite(Number(record.annualCost))
  );
  if (!hasUsableCost) return null;

  // CostLeakageReport groups records into dead rent and under-use, then adds
  // those two annualCost subtotals for its Total leakage KPI.
  const total = records.reduce((sum, record) => {
    const amount = Number(record?.annualCost);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  return Number.isFinite(total) ? total : null;
}

/**
 * @param {{
 *   onSelectReport: (key: string) => void,
 *   onNavListChange?: (keys: string[]) => void,
 * }} props
 */
export function ReportsPage({ onSelectReport, onNavListChange, currentRole }) {
  const spacePolicyState = useSpacePolicy();
  const featureContext = useFeatureFlagContext();
  const { reportFlags, flagsReady } = featureContext;
  const reportEligibility = useMemo(() => getReportNavEligibility(featureContext, currentRole), [featureContext, currentRole]);
  const orderedTiles = useMemo(() => {
    const keys = getOrderedReportKeys(reportFlags, flagsReady, reportEligibility.dataCoverage);
    return getAvailableReportKeys(keys, reportEligibility).map((key) => TILE_MAP[key]);
  }, [flagsReady, reportFlags, reportEligibility]);

  const coverageEnabled = orderedTiles.some((tile) => tile.key === "report-data-coverage");
  const [coverageMetric, setCoverageMetric] = useState("loading");
  useEffect(() => {
    if (!coverageEnabled) {
      setCoverageMetric(null);
      return undefined;
    }
    let live = true;
    setCoverageMetric("loading");
    // Let the report resolver choose its default reporting month.
    getDataCoverage({})
      .then((report) => {
        if (!live) return;
        const ratio = report?.portfolio?.coverageRatio;
        setCoverageMetric(typeof ratio === "number" && Number.isFinite(ratio)
          && /^\d{4}-(0[1-9]|1[0-2])$/.test(report?.selectedMonth ?? "")
          ? { value: ratio * 100, month: report.selectedMonth } : null);
      })
      .catch(() => { if (live) setCoverageMetric(null); });
    return () => { live = false; };
  }, [coverageEnabled]);

  // ─── Toolbar state (URL-encoded via useReportViewState) ───────────────────
  const [landingState, setLandingState] = useReportViewState({
    storageKey: REPORT_LANDING_KEY,
    fields: LANDING_VIEW_STATE_FIELDS,
  });
  const searchText     = landingState.q;
  const categoryFilter = landingState.category;
  const setSearchText     = useCallback((val) => setLandingState({ q: val }),        [setLandingState]);
  const setCategoryFilter = useCallback((val) => setLandingState({ category: val }), [setLandingState]);

  // Live role count for the Open Roles tile — fetched once on mount.
  // "loading" → actively fetching; null → failed or no usable value; number → resolved.
  const [openRolesCount, setOpenRolesCount] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-open-roles") || !reportEligibility.ready) {
      setOpenRolesCount(null);
      return undefined;
    }
    let cancelled = false;
    getOpenRolesReport()
      .then((data) => {
        if (cancelled) return;
        const total = data?.kpis?.totalReqs;
        if (Number.isFinite(total) && !data?.freshness?.ghUnavailable) setOpenRolesCount(total);
        else setOpenRolesCount(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch open-roles count", {
          name: err?.name,
          msg: err?.message,
        });
        setOpenRolesCount(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready]);

  // Same latest monthly, all-region company roll-up as the detail report's
  // default view. A missing attendance sample is not a measured zero.
  const [utilizationAttendance, setUtilizationAttendance] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-utilization") || !reportEligibility.ready || !reportEligibility.offices) {
      setUtilizationAttendance(null);
      return undefined;
    }
    let cancelled = false;
    setUtilizationAttendance("loading");
    getUtilizationReport({ period: "monthly" })
      .then((report) => {
        if (cancelled) return;
        const attendance = report?.company?.avgAttendancePct;
        setUtilizationAttendance(
          report?.offices?.length > 0 && typeof attendance === "number" && Number.isFinite(attendance)
            ? Math.round(attendance * 100)
            : null
        );
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch utilization attendance", {
          name: err?.name,
          msg: err?.message,
        });
        setUtilizationAttendance(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready, reportEligibility.offices]);

  // Live total-company headcount for the Headcount Trend tile — fetched once on mount.
  // "loading" → actively fetching; null → failed or no usable value; number → resolved.
  const [headcountTotal, setHeadcountTotal] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-headcount-trend") || !reportEligibility.ready) {
      setHeadcountTotal(null);
      return undefined;
    }
    let cancelled = false;
    getHeadcountTrendReport()
      .then((data) => {
        if (cancelled) return;
        const agg = data?.aggregate;
        if (Array.isArray(agg) && agg.length > 0) {
          const total = agg[agg.length - 1]?.totalCompany;
          if (Number.isFinite(total)) setHeadcountTotal(total);
          else setHeadcountTotal(null);
        } else {
          setHeadcountTotal(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch headcount total", {
          name: err?.name,
          msg: err?.message,
        });
        setHeadcountTotal(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready]);

  // Live Space Fit "over capacity" count for the Space Fit tile — fetched once
  // on mount. "loading" → actively fetching; null → failed or no usable value; number → resolved.
  const [spaceFitOverCount, setSpaceFitOverCount] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-space-fit") || !reportEligibility.ready || !reportEligibility.offices) {
      setSpaceFitOverCount(null);
      return undefined;
    }
    let cancelled = false;
    getSpaceFitReport()
      .then((report) => {
        if (cancelled) return;
        const over = report?.kpis?.over;
        if (typeof over === "number") setSpaceFitOverCount(over);
        else setSpaceFitOverCount(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch space fit counts", {
          name: err?.name,
          msg: err?.message,
        });
        setSpaceFitOverCount(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready, reportEligibility.offices, spacePolicyState.baseline]);

  // Live portfolio-wide average daily badge-ins for the latest completed
  // calendar month. The report response contains both the selected-office KPI
  // and the company roll-up; the landing headline must use the latter.
  // "loading" → actively fetching; null → failed or no usable value; number → resolved.
  const [footTrafficAvgDaily, setFootTrafficAvgDaily] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-foot-traffic") || !reportEligibility.ready || !reportEligibility.offices) {
      setFootTrafficAvgDaily(null);
      return undefined;
    }
    let cancelled = false;
    const range = previousCompletedCalendarMonthRange();
    getFootTrafficReport(range)
      .then((report) => {
        if (cancelled) return;
        const avgDaily = report?.company?.avgDaily;
        if (Number.isFinite(avgDaily)) setFootTrafficAvgDaily(avgDaily);
        else setFootTrafficAvgDaily(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch foot traffic average", {
          name: err?.name,
          msg: err?.message,
        });
        setFootTrafficAvgDaily(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready, reportEligibility.offices]);

  // Live annualized Cost Leakage total, using the same annualCost records as
  // the detail report. Restricted, empty, and malformed responses resolve to
  // the tile's neutral dash rather than suggesting there is no leakage.
  const [costLeakageTotalValue, setCostLeakageTotalValue] = useState("loading");

  useEffect(() => {
    if (!orderedTiles.some((tile) => tile.key === "report-cost-leakage") || !reportEligibility.ready) {
      setCostLeakageTotalValue(null);
      return undefined;
    }
    let cancelled = false;
    setCostLeakageTotalValue("loading");
    getCostLeakageReport()
      .then((report) => {
        if (!cancelled) setCostLeakageTotalValue(costLeakageTotal(report));
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch cost leakage total", {
          name: err?.name,
          msg: err?.message,
        });
        setCostLeakageTotalValue(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready]);

  // WPR RAG roll-up for the WPR tile banner — fetched once on mount using the
  // same data sources as WeeklyPortfolioReview (projects + locations + milestones).
  // buildWprRows derives ragColor for each row; we count by color bucket.
  // "loading" → actively fetching; null → failed; object → resolved.
  const [wprRagCounts, setWprRagCounts] = useState("loading");

  useEffect(() => {
    const hasWpr = orderedTiles.some((tile) => tile.key === "report-wpr");
    if (!reportEligibility.ready || !hasWpr || (!reportEligibility.projects && !reportEligibility.offices)) {
      setWprRagCounts(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      reportEligibility.projects ? getProjects() : Promise.resolve([]),
      reportEligibility.offices ? getLocations() : Promise.resolve([]),
      reportEligibility.projects ? getAllNextMilestones() : Promise.resolve([]),
    ])
      .then(([projs, locs, ms]) => {
        if (cancelled) return;
        const rows = buildWprRows(
          Array.isArray(projs) ? projs : [],
          Array.isArray(locs)  ? locs  : [],
          Array.isArray(ms)    ? ms    : [],
          new Map(),
        );
        // Bucket by rank (0=critical, 1=at_risk, 2=on_track, 3=unknown).
        // Rank is stable for both project rows (PROJECT_STATUS) and location
        // rows (health value), unlike ragColor which differs between the two
        // row types (T.greenDeep vs T.green, etc.).
        const onTrack  = rows.filter((r) => r.rank === 2).length;
        const atRisk   = rows.filter((r) => r.rank === 1).length;
        const critical = rows.filter((r) => r.rank === 0).length;
        setWprRagCounts({ onTrack, atRisk, critical });
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not fetch WPR RAG counts", {
          name: err?.name,
          msg: err?.message,
        });
        setWprRagCounts(null);
      });
    return () => { cancelled = true; };
  }, [orderedTiles, reportEligibility.ready, reportEligibility.projects, reportEligibility.offices]);

  // Favorites — a per-user set of starred report keys, persisted via
  // PUT /api/preferences/reports_favorites. "Favorite reports" toolbar
  // control toggles a favorites-only view of the grid.
  const [favoriteKeys, setFavoriteKeys] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const favoritesSaveTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getPreference("reports_favorites")
      .then((saved) => {
        if (cancelled) return;
        if (Array.isArray(saved)) setFavoriteKeys(saved.filter((k) => k in TILE_MAP));
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("ReportsPage: could not load reports_favorites preference", {
          name: err?.name,
          msg: err?.message,
        });
      });
    return () => { cancelled = true; };
  }, []);

  // Cancel any pending debounced favorites save on unmount.
  useEffect(() => {
    return () => {
      if (favoritesSaveTimerRef.current) clearTimeout(favoritesSaveTimerRef.current);
    };
  }, []);

  const toggleFavorite = useCallback((key) => {
    setFavoriteKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (favoritesSaveTimerRef.current) clearTimeout(favoritesSaveTimerRef.current);
      favoritesSaveTimerRef.current = setTimeout(() => {
        setPreference("reports_favorites", next).catch((err) => {
          log.error("[Error] Failed to persist reports_favorites", { name: err?.name, msg: err?.message });
        });
      }, 400);
      return next;
    });
  }, []);

  // ─── Ordered tile list ────────────────────────────────────────────────────
  // reportFlags comes from FeatureFlagContext, already sorted by
  // feature_flags.sort_order (the admin drag-reorder column).
  // Filter to enabled flags only, then map to catalog tile metadata.
  // ─── Report nav-list callback ─────────────────────────────────────────────
  // Fires whenever the ordered tile sequence changes so AppShell can derive
  // Prev/Next neighbours for report detail pages.
  // TOOLBAR_DISABLED: orderedTiles === visibleTiles in practice; when the
  // toolbar is re-enabled switch this to visibleTiles so the nav sequence
  // narrows with the active filter.
  useEffect(() => {
    if (typeof onNavListChange === "function") {
      onNavListChange(orderedTiles.map((t) => t.key));
    }
  }, [orderedTiles, onNavListChange]);

  // ─── Search + category + favorites filtering ─────────────────────────────
  const favoriteSet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);

  // TOOLBAR_DISABLED: visibleTiles filtering logic is preserved so re-enabling
  // the toolbar is a minimal diff, but the render loop below uses orderedTiles
  // directly to guarantee all enabled reports are visible regardless of any
  // residual URL params or persisted localStorage state.
  const visibleTiles = useMemo(() => {
    const q = searchText.trim();
    return orderedTiles.filter((tile) => {
      if (showFavoritesOnly && !favoriteSet.has(tile.key)) return false;
      if (categoryFilter !== "all" && REPORT_CATEGORIES[tile.key] !== categoryFilter) return false;
      if (!q) return true;
      return matchesFoldedQuery(q, tile.title, tile.subtitle, tile.description, tile.tags.join(" "));
    });
  }, [orderedTiles, searchText, categoryFilter, showFavoritesOnly, favoriteSet]);

  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const tile of orderedTiles) {
      const cat = REPORT_CATEGORIES[tile.key];
      if (cat && !seen.has(cat)) {
        seen.add(cat);
        opts.push(cat);
      }
    }
    return opts;
  }, [orderedTiles]);

  return (
    <div style={{ maxWidth: "1400px", padding: "24px 32px 40px", margin: "0 auto", width: "100%" }}>
      {/* TOOLBAR_DISABLED — search / category filter / favorites toolbar hidden.
          With only 3–4 reports the controls add noise rather than value. The state
          declarations (searchText, categoryFilter, showFavoritesOnly, favoriteKeys)
          and visibleTiles memo are preserved below so a future diff to re-enable is
          minimal. To restore: uncomment this block and pass onToggleFavorite back to
          each <ReportTile>.

      <div
        data-testid="reports-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: S.paneGap,
        }}
      >
        <SearchInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search reports…"
          testId="reports-search-input"
          width={240}
        />

        <select
          data-testid="reports-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 9,
            padding: "8px 10px",
            fontSize: T.fsSm5,
            color: T.dark,
            background: T.card,
            fontFamily: "inherit",
          }}
        >
          <option value="all">All categories</option>
          {categoryOptions.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <button
          type="button"
          data-testid="reports-favorites-toggle"
          aria-pressed={showFavoritesOnly}
          onClick={() => setShowFavoritesOnly((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: `1px solid ${showFavoritesOnly ? T.accent : T.border}`,
            background: showFavoritesOnly ? T.lightGray : T.card,
            borderRadius: 9,
            padding: "7px 12px",
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            color: showFavoritesOnly ? T.accent : T.mid,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Star size={14} fill={showFavoritesOnly ? "currentColor" : "none"} aria-hidden="true" />
          Favorite reports
        </button>
      </div>
      */}

      {/* Tile grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))",
        gap: S.paneGap,
        alignItems: "stretch",
      }}>
        {/* TOOLBAR_DISABLED: render orderedTiles directly so URL params / persisted
            localStorage state cannot silently hide reports with no UI to clear them.
            Switch back to visibleTiles when re-enabling the toolbar. */}
        {orderedTiles.map((tile) => {
          let cardTile = tile;
          if (tile.key === "report-open-roles" && !(reportEligibility.markets && reportEligibility.offices)) {
            cardTile = {
                ...tile,
                description: "Live Greenhouse requisitions, with department and remote hiring summaries.",
                tags: ["Hiring", "Greenhouse"],
            };
          } else if (tile.key === "report-headcount-trend" && !reportEligibility.offices) {
            cardTile = {
              ...tile,
              description: "How company headcount changed across every Workday import, with the aggregate company trend.",
            };
          } else if (tile.key === "report-utilization" && !reportEligibility.markets) {
            cardTile = {
              ...tile,
              description: "Home-aligned seat and attendance utilization across active operational offices, with period and source-aware rollups.",
            };
          } else if (tile.key === "report-wpr") {
            const scope = [
              reportEligibility.projects ? "Projects" : null,
              reportEligibility.offices ? "Sites" : null,
            ].filter(Boolean);
            const scopeLabel = scope.join(" & ") || "Portfolio";
            const scopedEntities = [
              reportEligibility.projects ? "active projects" : null,
              reportEligibility.offices ? "operational sites" : null,
            ].filter(Boolean).join(" and ");
            cardTile = {
              ...tile,
              subtitle: `${scopeLabel} Roll-up`,
              description: `RAG-sorted ${scopedEntities} with one editorial update, next milestone, and owner at a glance.`,
              tags: [
                reportEligibility.projects ? "Projects" : null,
                reportEligibility.offices ? "Sites" : null,
                "RAG Status",
              ].filter(Boolean),
            };
          }
          return (
            <ReportTile
              key={tile.key}
              tile={cardTile}
              onSelectReport={onSelectReport}
              isFavorite={favoriteSet.has(tile.key)}
              // onToggleFavorite omitted — TOOLBAR_DISABLED; star guard in ReportTile suppresses the button
              liveCount={
                tile.key === "report-data-coverage" ? (coverageEnabled ? coverageMetric?.value ?? coverageMetric : null) :
                tile.key === "report-open-roles"        ? openRolesCount :
                tile.key === "report-headcount-trend"   ? headcountTotal :
                tile.key === "report-space-fit"         ? spaceFitOverCount :
                tile.key === "report-utilization"       ? utilizationAttendance :
                tile.key === "report-cost-leakage"      ? costLeakageTotalValue :
                tile.key === "report-foot-traffic"      ? (
                  typeof footTrafficAvgDaily === "number"
                    ? Math.trunc(footTrafficAvgDaily)
                    : footTrafficAvgDaily
                ) :
                undefined
              }
              liveSuffix={
                tile.key === "report-data-coverage" ? "%" :
                tile.key === "report-utilization" ? "%" :
                tile.key === "report-cost-leakage" ? "/yr" :
                ""
              }
              liveFormat={tile.key === "report-cost-leakage" ? "compactCurrency" : "number"}
              livePrecision={tile.key === "report-data-coverage" ? 1 : undefined}
              liveLabel={
                tile.key === "report-data-coverage" ? `badging coverage - ${fmtShortMonth(coverageMetric?.month)}` :
                tile.key === "report-headcount-trend" ? (
                  reportEligibility.offices ? "office-assigned + remote · snapshot" : "company total · snapshot"
                ) :
                tile.key === "report-space-fit"       ? "over capacity" :
                tile.key === "report-open-roles"      ? "unique requisitions" :
                tile.key === "report-utilization"     ? "avg attendance · company" :
                tile.key === "report-foot-traffic"    ? "avg daily badge-ins · all sites" :
                tile.key === "report-cost-leakage"    ? "identified leakage" :
                "open roles"
              }
              ragCounts={tile.key === "report-wpr" ? wprRagCounts : undefined}
            />
          );
        })}
        {!flagsReady && (
          <div
            data-testid="reports-loading-state"
            style={{
              gridColumn: "1 / -1",
              textAlign: "center",
              padding: "40px 20px",
              color: T.mid,
              fontSize: T.fsSm5,
            }}
          >
            Checking report access…
          </div>
        )}
        {flagsReady && orderedTiles.length === 0 && (
          <div
            data-testid="reports-empty-state"
            style={{
              gridColumn: "1 / -1",
              textAlign: "center",
              padding: "40px 20px",
              color: T.mid,
              fontSize: T.fsSm5,
            }}
          >
            No reports are currently available.
          </div>
        )}
      </div>

    </div>
  );
}
